
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useUserAccountMutations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Send setup email mutation
  const resendVerificationMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke('send-user-setup-email', {
        body: {
          email,
          redirect_to: `${window.location.origin}/reset-password`
        }
      });
      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Failed to send setup email');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminActivityLogs'] });
      toast({
        title: "Setup email sent",
        description: "A setup email has been sent to the user."
      });
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ['adminActivityLogs'] });
      toast({
        title: "Error sending setup email",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Password reset email sent",
        description: "The user will receive an email with instructions to reset their password."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error sending reset email",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Disable 2FA for user mutation
  const disable2FAMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('disable-2fa', {
        body: { user_id: userId }
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Failed to disable 2FA');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
      toast({
        title: "2FA disabled",
        description: "Two-factor authentication has been disabled for the user."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error disabling 2FA",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  return {
    resendVerification: resendVerificationMutation.mutateAsync,
    resetUserPassword: resetPasswordMutation.mutate,
    disableUser2FA: disable2FAMutation.mutate,
  };
};
